/**
 * TODO: コンフリクトを避けるため一時的にshiver-appからハードコピーして使っているが、shiver-adminの開発が一段落ついたらshiver-coreに移し統合する
 */

import { PREMIUM_MAGAZINE_OPTION_KEYS } from 'shiver-core/static';
import { DocumentId, DocumentTypeKey, RegionKey } from 'shiver-core/types';
import { z } from 'zod';

export { DocumentId, DocumentForSearch } from 'shiver-core/types';

const nonNullDate = (fieldName: string) =>
  z
    .any()
    .refine((v) => v !== null, { message: `${fieldName} must not be null` })
    .pipe(z.coerce.date());

export const DocumentType = DocumentTypeKey;
export const Region = RegionKey;

export const SystemInfo = z
  .object({
    createdAt: nonNullDate('createdAt'),
    createdBy: z.string(),
    modifiedAt: nonNullDate('modifiedAt'),
    modifiedBy: z.string(),
  })
  // 全箇所で常にこれらの値を指定するのが手間なので、開発的な都合によりpartialにする
  .partial();
export type SystemInfo = z.infer<typeof SystemInfo>;

export const OperationId = z.string().brand('OperationId');
export type OperationId = z.infer<typeof OperationId>;

export const Category = z.object({
  id: z.string(),
  name: z.string().nonempty(),
});
export type Category = z.infer<typeof Category>;

export const Series = z
  .object({
    id: z.string(),
    name: z.string().nonempty(),
  })
  .extend(SystemInfo.shape);
export type Series = z.infer<typeof Series>;

export const Sector = z
  .object({
    id: z.string(),
    name: z.string().nonempty(),
    category: Category,
  })
  .extend(SystemInfo.shape);
export type Sector = z.infer<typeof Sector>;

export const Topic = z
  .object({
    id: z.string(),
    name: z.string().nonempty(),
    category: Category,
  })
  .extend(SystemInfo.shape);
export type Topic = z.infer<typeof Topic>;

export const PremiumMagazineOptionKey = z.enum(PREMIUM_MAGAZINE_OPTION_KEYS);
export type PremiumMagazineOptionKey = z.infer<typeof PremiumMagazineOptionKey>;

export const AuthorId = z.string().brand('AuthorId');
export type AuthorId = z.infer<typeof AuthorId>;

export const Author = z
  .object({
    id: AuthorId,
    name: z.string(),
    organization: z.string().nonempty().optional().nullable(),
    jobTitle: z.string().nonempty().optional().nullable(),
    description: z.string().nonempty().optional().nullable(),
    imageKey: z.string().optional().nullable(),
  })
  .extend(SystemInfo.shape);
export type Author = z.infer<typeof Author>;

export const AttachmentId = z.string().brand('AttachmentId');
export type AttachmentId = z.infer<typeof AttachmentId>;

export const Attachment = z.object({
  id: AttachmentId,
  title: z.string().nonempty(),
  url: z.string().nonempty(),
  /**
   * @deprecated 開発当初はソースとなるURLの公開日時を設定する想定だったが公開日時を取得する方法が現時点でないため
   *             現状は関連資料の登録日時（new Date()）が設定されている。
   */
  publishedAt: z.coerce.date(),
  publishedBy: z.string().nullable(), // nullになる可能性あるか？
});
export type Attachment = z.infer<typeof Attachment>;

export const TimelineId = z.string().brand('TimelineId');
export type TimelineId = z.infer<typeof TimelineId>;

export const TimelineHead = z
  .object({
    id: TimelineId,
    name: z.string().nonempty(),
    sectors: z.array(Sector),
    topics: z.array(Topic),
  })
  .extend(SystemInfo.shape);
export type TimelineHead = z.infer<typeof TimelineHead>;

export const DocumentHead = z
  .object({
    id: DocumentId,
    title: z.string().nonempty(),
    lede: z.string().nonempty().nullable(),
    featuredImageKey: z.string().nullable(),
    thumbnailImageKey: z.string().nullable(),
    regions: z.array(Region),
    authors: z.array(Author),
    sectors: z.array(Sector),
    topics: z.array(Topic),
    series: z.array(Series),
    timelines: z.array(TimelineHead.pick({ id: true, name: true })),
    type: DocumentType,
    isFeatured: z.boolean(),
    isSpecialOpen: z.boolean().default(false),
    publishedAt: z.coerce.date().nullable(),
    updatedAt: z.coerce.date().nullable(),
    tkcLawLibraryUrl: z.string().nullable(),
    westlawJapanUrl: z.string().nullable(),
    shojiHomuDbUrl: z.string().nullable(),
    junkanShojiHomuDbUrl: z.string().nullable(),
  })
  .extend(SystemInfo.shape);
export type DocumentHead = z.infer<typeof DocumentHead>;

export const DocumentBody = z.object({
  attachments: z.array(Attachment),
  relatedDocuments: z.array(DocumentHead),
  textHtml: z.string().nonempty().nullable(),
});
export type DocumentBody = z.infer<typeof DocumentBody>;

// 雑誌記事（アクセス権限あり）
export const MagazineArticleBody = z.object({
  textHtml: z.null(),
  documentId: DocumentId,
  magazineTocId: DocumentId.nullable(),
  magazineKey: PremiumMagazineOptionKey,
  pdfFileKey: z.string(),
  order: z.number().int().nonnegative().default(0),
  tocSeriesIds: z.array(z.string()).optional(),
});
export type MagazineArticleBody = z.infer<typeof MagazineArticleBody>;

//雑誌目次
export const MagazineTocBody = z.object({
  textHtml: z.null(),
  magazineArticles: z.array(DocumentHead),
});
export type MagazineTocBody = z.infer<typeof MagazineTocBody>;

export const TimelineDocumentHead = DocumentHead.pick({
  id: true,
  publishedAt: true,
  title: true,
  authors: true,
  regions: true,
  sectors: true,
  topics: true,
  series: true,
  type: true,
  isFeatured: true,
}).extend({
  // 要チェック記事用のフィールド
  isPinned: z.boolean(),
  pinnedLabel: z.string().nonempty().nullable(),
});
export type TimelineDocumentHead = z.infer<typeof TimelineDocumentHead>;

export const MilestoneId = z.string().brand('MilestoneId');
export type MilestoneId = z.infer<typeof MilestoneId>;

export const MilestoneType = z.enum([
  'council',
  'diet',
  'conclusion',
  'preparation',
  'enforcement',
  'post-enforcement',
]);
export type MilestoneType = z.infer<typeof MilestoneType>;

export const Milestone = z.object({
  id: MilestoneId,
  type: MilestoneType,
  name: z.string().nonempty(),
  description: z.string().nonempty().nullable(),
  documents: z.array(TimelineDocumentHead),
});
export type Milestone = z.infer<typeof Milestone>;

export const MilestoneList = z.array(Milestone).refine(
  (list) => {
    if (list[0]?.type !== 'council') {
      return false;
    }

    for (let i = 0; i < list.length; i += 1) {
      const current = list[i]!.type;
      const next = list[i + 1]?.type;

      switch (current) {
        case 'council':
          if (next !== 'diet' && next !== 'conclusion') {
            return false;
          }
          break;
        case 'diet':
          if (next !== 'conclusion') {
            return false;
          }
          break;
        case 'conclusion':
          if (next !== 'preparation') {
            return false;
          }
          break;

        case 'preparation':
          if (next !== 'enforcement') {
            return false;
          }

          break;
        case 'enforcement':
          if (next !== 'post-enforcement' && next !== 'preparation') {
            return false;
          }
          break;
        case 'post-enforcement':
          if (next !== undefined) {
            return false;
          }
          break;
      }
    }

    return true;
  },
  {
    message: 'マイルストーンの構成が不正です',
  },
);
export type MilestoneList = z.infer<typeof MilestoneList>;

export const TimelineBody = z.object({
  id: TimelineId,
  description: z.string().nonempty().nullable(),
  milestones: MilestoneList,
});
export type TimelineBody = z.infer<typeof TimelineBody>;

export const Timeline = TimelineHead.merge(TimelineBody);
export type Timeline = z.infer<typeof Timeline>;

export const DocumentBase = DocumentHead.extend(DocumentBody.shape);
export type DocumentBase = z.infer<typeof DocumentBase>;

export const DefaultDocument = DocumentBase.extend({
  type: DocumentType.exclude(['magazine_article', 'magazine_toc']),
});
export type DefaultDocument = z.infer<typeof DefaultDocument>;

export const MagazineArticle = DocumentBase.extend({
  type: z.literal('magazine_article'),
  ...MagazineArticleBody.shape,
});
export type MagazineArticle = z.infer<typeof MagazineArticle>;

export const MagazineToc = DocumentBase.extend({
  type: z.literal('magazine_toc'),
  ...MagazineTocBody.shape,
});
export type MagazineToc = z.infer<typeof MagazineToc>;

export const Document = z.discriminatedUnion('type', [
  DefaultDocument.strict(),
  MagazineArticle.strict(),
  MagazineToc.strict(),
]);
export type Document = z.infer<typeof Document>;

type Mask<Keys extends PropertyKey> = {
  [K in Keys]?: true;
};
export const omitDocument = <K extends keyof Document>(mask: Mask<K>) =>
  z.discriminatedUnion('type', [
    DefaultDocument.strict().omit(mask),
    MagazineArticle.strict().omit(mask),
    MagazineToc.strict().omit(mask),
  ]);

export const MagazineAnnouncement = z.object({
  text: z.string().nonempty(),
  magazineTocDocument: DocumentHead,
});
export type MagazineAnnouncement = z.infer<typeof MagazineAnnouncement>;

export const TopPickup = z.object({
  documents: z.array(DocumentHead).min(0).max(5),
  timelines: z.array(TimelineHead).min(0).max(15),
  magazineAnnouncements: z.array(MagazineAnnouncement).min(0).max(3),
});
export type TopPickup = z.infer<typeof TopPickup>;
